/**
 * ObstacleField — alien asteroid bodies scattered densely across the world.
 *
 * Each asteroid is a fixed lump of rock; the cluster takes a hit if it
 * touches one. Asteroids anchor the food orbits — pellets circle each
 * one in a ring band. The field is dense enough that any view of the
 * world frames at least two asteroids, so the world reads as a debris
 * field rather than an empty bowl with the occasional rock.
 *
 * Look
 * ----
 * Each asteroid is rendered as a stack of inset polygons offset toward
 * a light source: shadow base → mid tone → lit face → highlight cap →
 * specular pin-point. That's a stylized 3D-rendering trick — the
 * shape doesn't have geometry, but the stepped insets read as a
 * curved surface catching light. Each asteroid rolls a random
 * rotation, aspect ratio, vertex count, and palette so adjacent
 * asteroids never look identical.
 */

import { Container, Graphics } from 'pixi.js';

interface Palette {
  /** Outer silhouette — deepest shade, sits against the bg. */
  shadow: number;
  /** Mid tone — the body of the rock as seen from a glancing angle. */
  mid: number;
  /** Lit face — the side turned toward the light source. */
  lit: number;
  /** Highlight cap — the brightest broad area. */
  hi: number;
  /** Specular pin-point — the wet-shine spot, near-white. */
  spec: number;
}

export interface Obstacle {
  /** Live world position. Updated each frame by (vx, vy). */
  cx: number;
  cy: number;
  /** Linear velocity in world px/s. Asteroids roam freely and wrap
   *  around the world bounds; pairwise collisions at MIN_SEPARATION
   *  keep them from clumping. */
  vx: number;
  vy: number;
  /** Signed angular velocity in rad/s. Random sign per asteroid so
   *  neighbouring rocks rotate independently. */
  rotRate: number;
  /** World-space radius for collision (matches the visual outer hull). */
  radius: number;
  g: Graphics;
}

const RADIUS_MIN = 110;
const RADIUS_MAX = 175;
// Tighter packing — ~30 asteroids across the 4800² world, dense enough
// that the player's view nearly always frames at least two of them.
const MIN_SEPARATION = 760;
const WORLD_MARGIN = 380;
// Asteroids never spawn within this many pixels of the cluster.
const SAFE_SPAWN_DIST = 640;

// Per-asteroid linear roaming speed. Range matches the previous
// sinusoidal drift's peak speed (driftAmp × driftFreq, ~0.9–7.6 px/s)
// so the world still feels like ocean-current pacing, not bouncing.
const ROAM_SPEED_MIN = 1.2;
const ROAM_SPEED_MAX = 7.5;
// Slow tumble — visible at a glance but never dizzying. Range gives
// a full rotation in roughly 35-110 seconds. Sign is randomized per
// asteroid so neighbours rotate independently.
const ROT_RATE_MIN = 0.055;
const ROT_RATE_MAX = 0.180;

function lerpColor(c1: number, c2: number, t: number): number {
  const r1 = (c1 >> 16) & 0xff, g1 = (c1 >> 8) & 0xff, b1 = c1 & 0xff;
  const r2 = (c2 >> 16) & 0xff, g2 = (c2 >> 8) & 0xff, b2 = c2 & 0xff;
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return (r << 16) | (g << 8) | b;
}

const PALETTES: Palette[] = [
  // Amethyst (violet)
  { shadow: 0x140a28, mid: 0x3a1f5e, lit: 0x7e54c0, hi: 0xc4a8ff, spec: 0xfff4ff },
  // Cryo (deep cyan ice)
  { shadow: 0x081a2c, mid: 0x1a3e5e, lit: 0x4ea0c8, hi: 0xb4e8ff, spec: 0xffffff },
  // Veridian (toxic green)
  { shadow: 0x081f12, mid: 0x1c4a30, lit: 0x52b070, hi: 0xb8ffc8, spec: 0xffffff },
  // Carmine (alien magenta)
  { shadow: 0x2a0a1c, mid: 0x6a1e44, lit: 0xc8528a, hi: 0xffa8d8, spec: 0xfff4f8 },
  // Sulphur (pale ochre)
  { shadow: 0x251a06, mid: 0x584012, lit: 0xb8862c, hi: 0xfde08a, spec: 0xfffce0 },
];

export class ObstacleField {
  private parent: Container;
  private worldW: number;
  private worldH: number;
  private obstacles: Obstacle[] = [];

  constructor(
    parent: Container,
    worldW: number,
    worldH: number,
    count: number,
    clusterX: number,
    clusterY: number,
  ) {
    this.parent = parent;
    this.worldW = worldW;
    this.worldH = worldH;
    let seed = 0xDEADC0DE;
    const rand = () => {
      seed = (seed * 1664525 + 1013904223) | 0;
      return ((seed >>> 0) % 100000) / 100000;
    };
    this.obstacles = this.layout(count, clusterX, clusterY, rand);
    for (const o of this.obstacles) this.draw(o, rand);
  }

  private layout(count: number, clusterX: number, clusterY: number, rand: () => number): Obstacle[] {
    const out: Obstacle[] = [];
    const minSepSq = MIN_SEPARATION * MIN_SEPARATION;
    const safeSq = SAFE_SPAWN_DIST * SAFE_SPAWN_DIST;
    let attempts = 0;
    while (out.length < count && attempts < 4000) {
      attempts++;
      const cx = WORLD_MARGIN + rand() * (this.worldW - 2 * WORLD_MARGIN);
      const cy = WORLD_MARGIN + rand() * (this.worldH - 2 * WORLD_MARGIN);
      const dxc = cx - clusterX;
      const dyc = cy - clusterY;
      if (dxc * dxc + dyc * dyc < safeSq) continue;
      let ok = true;
      for (const o of out) {
        const dx = o.cx - cx;
        const dy = o.cy - cy;
        if (dx * dx + dy * dy < minSepSq) { ok = false; break; }
      }
      if (!ok) continue;
      const radius = RADIUS_MIN + rand() * (RADIUS_MAX - RADIUS_MIN);
      const g = new Graphics();
      this.parent.addChild(g);
      const speed = ROAM_SPEED_MIN + rand() * (ROAM_SPEED_MAX - ROAM_SPEED_MIN);
      const heading = rand() * Math.PI * 2;
      const rotMag = ROT_RATE_MIN + rand() * (ROT_RATE_MAX - ROT_RATE_MIN);
      const rotRate = rand() < 0.5 ? -rotMag : rotMag;
      out.push({
        cx, cy,
        vx: Math.cos(heading) * speed,
        vy: Math.sin(heading) * speed,
        rotRate,
        radius, g,
      });
    }
    return out;
  }

  /**
   * Tick roaming + rotation. Each asteroid moves along its (vx, vy)
   * vector and wraps around the world bounds. A pairwise pass keeps
   * any two asteroids from getting closer than MIN_SEPARATION by
   * pushing them apart and reflecting their velocities along the
   * collision normal — speeds are preserved (elastic, equal mass).
   * Food orbits and cluster collision read live cx/cy each frame, so
   * everything follows automatically.
   */
  update(dt: number): void {
    const ww = this.worldW;
    const wh = this.worldH;
    for (const o of this.obstacles) {
      o.cx += o.vx * dt;
      o.cy += o.vy * dt;
      // Toroidal wrap — fall off one edge, appear on the opposite.
      if (o.cx < 0) o.cx += ww;
      else if (o.cx >= ww) o.cx -= ww;
      if (o.cy < 0) o.cy += wh;
      else if (o.cy >= wh) o.cy -= wh;
      o.g.position.set(o.cx, o.cy);
      o.g.rotation += o.rotRate * dt;
    }
    // Pairwise separation. Distance is measured on the torus (shortest
    // wrap), so two asteroids near opposite edges still see each other
    // and don't collapse together at the seam.
    const minSep = MIN_SEPARATION;
    const minSepSq = minSep * minSep;
    const halfW = ww * 0.5;
    const halfH = wh * 0.5;
    for (let i = 0; i < this.obstacles.length; i++) {
      const a = this.obstacles[i];
      for (let j = i + 1; j < this.obstacles.length; j++) {
        const b = this.obstacles[j];
        let dx = b.cx - a.cx;
        let dy = b.cy - a.cy;
        if (dx > halfW) dx -= ww; else if (dx < -halfW) dx += ww;
        if (dy > halfH) dy -= wh; else if (dy < -halfH) dy += wh;
        const dSq = dx * dx + dy * dy;
        if (dSq >= minSepSq || dSq < 0.0001) continue;
        const d = Math.sqrt(dSq);
        const nx = dx / d;
        const ny = dy / d;
        // Push each half the overlap so they reach min separation
        // again. Apply wrap after the push so we don't end up outside
        // bounds.
        const half = (minSep - d) * 0.5;
        a.cx -= nx * half; a.cy -= ny * half;
        b.cx += nx * half; b.cy += ny * half;
        if (a.cx < 0) a.cx += ww; else if (a.cx >= ww) a.cx -= ww;
        if (a.cy < 0) a.cy += wh; else if (a.cy >= wh) a.cy -= wh;
        if (b.cx < 0) b.cx += ww; else if (b.cx >= ww) b.cx -= ww;
        if (b.cy < 0) b.cy += wh; else if (b.cy >= wh) b.cy -= wh;
        // Elastic reflection along the normal. Equal mass → swap the
        // normal-axis components, which preserves each asteroid's
        // speed magnitude.
        const va = a.vx * nx + a.vy * ny;
        const vb = b.vx * nx + b.vy * ny;
        const diff = vb - va;
        a.vx += diff * nx;
        a.vy += diff * ny;
        b.vx -= diff * nx;
        b.vy -= diff * ny;
      }
    }
  }

  private draw(o: Obstacle, rand: () => number): void {
    const g = o.g;
    g.clear();
    g.position.set(o.cx, o.cy);
    const R = o.radius;

    const palette = PALETTES[Math.floor(rand() * PALETTES.length)];
    const rotation = rand() * Math.PI * 2;

    // Aspect: roughly a third tall, a third flat, a third round.
    // Variety in silhouette matters more than the geometry of any
    // single rock — the field reads as varied alien debris.
    const aspectRoll = rand();
    let aspectX: number, aspectY: number;
    if (aspectRoll < 0.33) {
      aspectX = 0.60 + rand() * 0.14; aspectY = 1.05 + rand() * 0.05;
    } else if (aspectRoll < 0.66) {
      aspectX = 1.05 + rand() * 0.05; aspectY = 0.55 + rand() * 0.14;
    } else {
      aspectX = 0.92 + rand() * 0.12; aspectY = 0.92 + rand() * 0.12;
    }

    const verts = 14 + Math.floor(rand() * 6);
    const angles: number[] = [];
    const radii: number[] = [];
    for (let i = 0; i < verts; i++) {
      angles.push((i / verts) * Math.PI * 2);
      radii.push(R * (0.84 + rand() * 0.22));
    }

    // Apply per-rock rotation + non-uniform scale to a local point.
    const tx = (lx: number, ly: number): [number, number] => {
      const sx = lx * aspectX;
      const sy = ly * aspectY;
      const c = Math.cos(rotation);
      const s = Math.sin(rotation);
      return [sx * c - sy * s, sx * s + sy * c];
    };

    // Build a polygon ring at scale `s` with center offset (offX, offY)
    // applied BEFORE rotation. The offset is what makes the inset
    // layers shift toward the light source.
    const ring = (s: number, offX: number, offY: number): Array<[number, number]> => {
      const out: Array<[number, number]> = [];
      for (let i = 0; i < verts; i++) {
        const lx = Math.cos(angles[i]) * radii[i] * s + offX;
        const ly = Math.sin(angles[i]) * radii[i] * s + offY;
        out.push(tx(lx, ly));
      }
      return out;
    };

    // Soft outer fade — many rings just larger than the silhouette,
    // shadow-colored with decreasing alpha. Lets the rock dissolve
    // into the bg instead of cutting against it with a hard edge.
    // Wider reach (out to 1.22×) and a smooth t² alpha falloff so
    // the outermost rings are barely visible — the rock blurs into
    // the water instead of sitting on top of it.
    const FADE_LAYERS = 10;
    for (let i = FADE_LAYERS; i >= 1; i--) {
      const t = i / FADE_LAYERS;             // 1 outermost → 0.1 inner
      const scale = 1.0 + 0.22 * t;
      const alpha = 0.16 * (1 - t) * (1 - t);
      this.poly(g, ring(scale, 0, 0));
      g.fill({ color: palette.shadow, alpha });
    }

    // Smooth 3D shading. Light is at upper-left in pre-rotation space.
    // We sweep many thin inset rings from the silhouette inward, with
    // each ring's color, scale, and lit-ward offset interpolated along
    // the palette stops — that turns what used to be 4 visible bands
    // into a continuous gradient from shadow rim to bright cap.
    const stops: Array<{ t: number; c: number }> = [
      { t: 0.00, c: palette.shadow },
      { t: 0.36, c: palette.mid },
      { t: 0.70, c: palette.lit },
      { t: 1.00, c: palette.hi },
    ];
    const sampleStops = (t: number): number => {
      for (let i = 1; i < stops.length; i++) {
        if (t <= stops[i].t) {
          const t0 = stops[i - 1].t;
          const t1 = stops[i].t;
          const local = t1 === t0 ? 0 : (t - t0) / (t1 - t0);
          return lerpColor(stops[i - 1].c, stops[i].c, local);
        }
      }
      return stops[stops.length - 1].c;
    };

    const LAYERS = 16;
    for (let i = 0; i < LAYERS; i++) {
      const t = i / (LAYERS - 1);
      const scale = 1.0 + (0.36 - 1.0) * t;
      const offX = -R * 0.22 * t;
      const offY = -R * 0.22 * t;
      this.poly(g, ring(scale, offX, offY));
      g.fill({ color: sampleStops(t) });
    }

    // Specular pin-point — small bright ellipse at the very lit-most
    // spot. The "wet shine" — what makes the rock look polished
    // rather than chalk.
    const [specX, specY] = tx(-R * 0.30, -R * 0.30);
    g.ellipse(specX, specY, R * 0.10, R * 0.055)
      .fill({ color: palette.spec, alpha: 0.88 });
    // Tinier inner core for an extra-bright pin.
    g.ellipse(specX, specY, R * 0.045, R * 0.020)
      .fill({ color: 0xffffff, alpha: 0.95 });

    // No crater pits — they read as holes punched in the rock and
    // pulled the eye away from the gradient. Surface variation is
    // implied entirely by the smooth shading + spec pin now.
    //
    // No silhouette stroke either — the outer fade rings carry the
    // edge softly, and a stroke would re-introduce the hard pop we
    // were trying to lose.
  }

  private poly(g: Graphics, pts: Array<[number, number]>): void {
    for (let i = 0; i < pts.length; i++) {
      const [x, y] = pts[i];
      if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
    }
    g.lineTo(pts[0][0], pts[0][1]);
  }

  /** Read-only access — used by NutrientField to anchor food orbits. */
  list(): ReadonlyArray<Obstacle> {
    return this.obstacles;
  }

  /**
   * Returns the position of the nearest asteroid the cluster overlaps,
   * or null. Asteroids aren't consumed — they sit there forever.
   */
  collideWith(cx: number, cy: number, r: number): { x: number; y: number } | null {
    for (const o of this.obstacles) {
      const dx = o.cx - cx;
      const dy = o.cy - cy;
      const reach = r + o.radius * 0.92;
      if (dx * dx + dy * dy <= reach * reach) {
        return { x: o.cx, y: o.cy };
      }
    }
    return null;
  }

  destroy(): void {
    for (const o of this.obstacles) o.g.destroy();
    this.obstacles = [];
  }
}
